import { useState, useRef, useEffect, useCallback } from "react"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"
import { restoreAuth } from "@/modules/auth/store/authSlice"
import { broadcastLogin } from "@/lib/session-sync"
import { toast } from "sonner"

// Small immutable value object for OTP digits
class OTPValue {
  readonly digits: string[]
  constructor(digits: string[] = Array(6).fill("")) {
    this.digits = digits
  }
  static empty() {
    return new OTPValue()
  }
  setAt(index: number, value: string) {
    const next = [...this.digits]
    next[index] = value
    return new OTPValue(next)
  }
  withDigits(digits: string[]) {
    const next = Array(6).fill("")
    digits.slice(0, 6).forEach((d, i) => (next[i] = d))
    return new OTPValue(next)
  }
  join() {
    return this.digits.join("")
  }
}

export default function OTPForm() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getToken } = useRecaptcha()
  const [otp, setOtp] = useState<OTPValue>(() => OTPValue.empty())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [resendTimer, setResendTimer] = useState(60)
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const email = sessionStorage.getItem("signupEmail") ?? ""

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendTimer])

  useEffect(() => {
    if (!cooldownUntil) return
    const tick = () => {
      if (Date.now() >= cooldownUntil) {
        setCooldownUntil(null)
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [cooldownUntil])

  const isCoolingDown = cooldownUntil !== null && Date.now() < cooldownUntil

  const applyRateLimitCooldown = useCallback((headers: Headers) => {
    const retryAfter = parseInt(headers.get('retry-after') ?? '60', 10)
    toast.error("Too many requests. Please try again later.")
    setCooldownUntil(Date.now() + retryAfter * 1000)
  }, [])

  const handleChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return

    setOtp((prev) => prev.setAt(index, value))
    setError("")

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp.digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text").slice(0, 6)

    if (!/^\d+$/.test(pastedData)) return

    const chars = pastedData.split("")
    setOtp((prev) => prev.withDigits(chars))

    // Focus last filled input or last input
    const nextIndex = Math.min(pastedData.length, 5)
    inputRefs.current[nextIndex]?.focus()
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const otpValue = otp.join()

    if (otpValue.length !== 6) {
      setError("Please enter all 6 digits")
      return
    }

    setIsSubmitting(true)

    const email = sessionStorage.getItem("signupEmail")
    if (!email) {
      setError("Session expired. Please try signing up again.")
      setIsSubmitting(false)
      return
    }

    try {
      const captchaToken = await getToken("validate_otp")
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/validate-otp`,
        {
          method: "PUT",
          // Include credentials so the httpOnly auth cookies the server sets
          // on successful validation are stored, logging the user in.
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            otp: otpValue,
            captchaToken,
          }),
        }
      )

      if (!response.ok) {
        if (response.status === HttpStatusCodes.TOO_MANY_REQUESTS) {
          applyRateLimitCooldown(response.headers)
          return
        }
        let errorMessage = "Invalid OTP. Please try again."
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorMessage
        } catch {
          errorMessage = response.statusText || errorMessage
        }
        setError(errorMessage)
        return
      }

      // Success - OTP validated. The server has set the auth cookies, so the
      // user is now logged in. Hydrate the auth state from the response (no
      // tokens are exposed here — they live only in the httpOnly cookies) and
      // take them straight to the dashboard, mirroring the post-login flow.
      const data = await response.json()
      const authUser = {
        user: {
          id: data.user.id,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email,
          status: data.user.status,
          createdAt: data.user.createdAt,
          lastLoginAt: data.user.lastLoginAt,
          needsLegalConsent: data.user.needsLegalConsent,
          organizationRole: data.user.organizationRole,
          organizationRoles: data.user.organizationRoles,
        },
        roles: data.roles,
        permissions: data.permissions,
        currentOrganizationId: data.user.currentOrganizationId || null,
      }

      sessionStorage.removeItem("signupEmail")
      dispatch(restoreAuth({ user: authUser, loginTimestamp: Date.now() }))
      broadcastLogin(authUser.user.email ?? "")

      navigate(authUser.user.needsLegalConsent ? "/accept-legal-documents" : "/dashboard", {
        replace: true,
      })
    } catch (error) {
      console.error("OTP validation error:", error)
      setError("Network error. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    const email = sessionStorage.getItem("signupEmail")
    if (!email) {
      setError("Session expired. Please try signing up again.")
      return
    }

    try {
      const captchaToken = await getToken("resend_otp")
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/resend-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            captchaToken,
          }),
        }
      )

      if (!response.ok) {
        if (response.status === HttpStatusCodes.TOO_MANY_REQUESTS) {
          applyRateLimitCooldown(response.headers)
          return
        }
        let errorMessage = "Failed to resend OTP. Please try again."
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorMessage
        } catch {
          errorMessage = response.statusText || errorMessage
        }
        setError(errorMessage)
        return
      }

      // Success - reset form and start timer
      setResendTimer(60)
      setError("")
      setOtp(OTPValue.empty())
      inputRefs.current[0]?.focus()
      toast.success(
        email
          ? `A new verification code has been sent to ${email}.`
          : "A new verification code has been sent."
      )
    } catch (error) {
      console.error("Resend OTP error:", error)
      setError("Network error. Please try again.")
    }
  }

  return (
    <div className="w-full">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Enter verification code</h1>
        <p className="text-base text-muted-foreground">
          We've sent a 6-digit code to{" "}
          {email
            ? <span className="font-medium text-foreground">{email}</span>
            : "your email address"}
          . Please enter it below.
        </p>
      </div>

      <div className="mt-8">
        <form onSubmit={handleSubmit} id="otp-form" className="space-y-6">
          <div className="flex gap-4 justify-center" onPaste={handlePaste}>
            {otp.digits.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl font-semibold"
                required
              />
            ))}
          </div>

          {error && <p className="text-sm text-red-600 text-center">{error}</p>}

          <div className="text-center">
            {resendTimer > 0 ? (
              <p className="text-sm text-muted-foreground">
                Resend code in{" "}
                <span className="font-semibold">{resendTimer}s</span>
              </p>
            ) : (
              <Button
                type="button"
                variant="link"
                onClick={handleResend}
                className="text-sm font-medium hover:underline"
                style={{ color: "#1A237E" }}
              >
                Resend verification code
              </Button>
            )}
          </div>
        </form>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <Button
          type="submit"
          form="otp-form"
          className="w-full text-white py-6 text-base"
          style={{ backgroundColor: "#1A237E" }}
          disabled={isSubmitting || isCoolingDown}
        >
          {isCoolingDown
            ? "Try again later"
            : isSubmitting
              ? "Verifying..."
              : "Verify code"}
        </Button>

        <Button
          type="button"
          variant="link"
          onClick={() => navigate("/login")}
          className="text-sm text-muted-foreground hover:text-primary transition-all duration-200"
        >
          Back to login
        </Button>
      </div>
    </div>
  )
}
